import type {
  AddHouseholdMemberRequest,
  CreateCustomFieldDefinitionRequest,
  CreateHouseholdRequest,
  CreatePersonContactRequest,
  CreatePersonRequest,
  CreateRelationshipRequest,
  CustomFieldDefinitionResponse,
  DeletePersonResponse,
  HouseholdDetailResponse,
  ImportPeopleRequest,
  ImportPeopleResponse,
  PaginatedResponse,
  PersonBirthdayResponse,
  PersonCatalogEntryResponse,
  PersonCatalogKind,
  PersonCustomFieldResponse,
  PersonDetailResponse,
  PersonRelationshipResponse,
  PersonSummaryResponse,
  TagSummaryResponse,
  UpdateCustomFieldDefinitionRequest,
  UpdatePersonContactRequest,
  UpdatePersonRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

/**
 * The People endpoints.
 *
 * Request and response types come from `@tabita/contracts` — the same
 * definitions the server validates against, so a contract change breaks the
 * client's build rather than its runtime.
 */
export interface ListPeopleParams {
  readonly search?: string;
  readonly status?: string;
  readonly campusId?: string;
  readonly tagId?: string;
  readonly gender?: string;
  readonly maritalStatus?: string;
  readonly birthDateFrom?: string;
  readonly birthDateTo?: string;
  readonly includeArchived?: boolean;
  readonly includeDeleted?: boolean;
  readonly cursor?: string;
  readonly limit?: number;
  readonly sort?: 'name' | '-name' | 'createdAt' | '-createdAt';
}

export const peopleApi = {
  list(params: ListPeopleParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<PersonSummaryResponse>>('/people', {
      query: { ...params },
      signal,
    });
  },

  get(id: string, signal?: AbortSignal) {
    return apiRequest<PersonDetailResponse>(`/people/${id}`, { signal });
  },

  create(body: CreatePersonRequest) {
    return apiRequest<PersonDetailResponse>('/people', { method: 'POST', body });
  },

  update(id: string, body: UpdatePersonRequest) {
    return apiRequest<PersonDetailResponse>(`/people/${id}`, { method: 'PATCH', body });
  },

  archive(id: string, reason: string) {
    return apiRequest<PersonDetailResponse>(`/people/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restore(id: string) {
    return apiRequest<PersonDetailResponse>(`/people/${id}/restore`, { method: 'POST' });
  },

  /** A real deletion, but only when nothing elsewhere references this
   *  person — see ADR-0006's amendment. `outcome: 'MARKED_DELETED'` means
   *  nothing was removed; the person was marked `DELETED` instead. */
  delete(id: string, reason: string) {
    return apiRequest<DeletePersonResponse>(`/people/${id}`, {
      method: 'DELETE',
      body: { reason },
    });
  },

  listTags(signal?: AbortSignal) {
    return apiRequest<readonly TagSummaryResponse[]>('/people/tags', { signal });
  },

  listUpcomingBirthdays(
    params: { withinDays?: number; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<readonly PersonBirthdayResponse[]>('/people/birthdays/upcoming', {
      query: { ...params },
      signal,
    });
  },

  /** `rows` are already parsed CSV rows — see `import-people-dialog.tsx`;
   *  the server never sees the raw file, only structured JSON. */
  importPeople(body: ImportPeopleRequest) {
    return apiRequest<ImportPeopleResponse>('/people/import', { method: 'POST', body });
  },

  // --- Households -------------------------------------------------------------

  createHousehold(body: CreateHouseholdRequest) {
    return apiRequest<HouseholdDetailResponse>('/people/households', { method: 'POST', body });
  },

  addHouseholdMember(householdId: string, body: AddHouseholdMemberRequest) {
    return apiRequest<HouseholdDetailResponse>(`/people/households/${householdId}/members`, {
      method: 'POST',
      body,
    });
  },

  removeHouseholdMember(householdId: string, personId: string) {
    return apiRequest<HouseholdDetailResponse>(
      `/people/households/${householdId}/members/${personId}`,
      { method: 'DELETE' },
    );
  },

  // --- Relationships ------------------------------------------------------------

  /** Another person's own relationships — used to derive the family tree's
   *  second-degree rows (grandparents, aunts/uncles, grandchildren) from a
   *  relative's own direct links, since those are never stored twice. */
  listRelationships(personId: string, signal?: AbortSignal) {
    return apiRequest<PersonRelationshipResponse[]>(`/people/${personId}/relationships`, {
      signal,
    });
  },

  createRelationship(personId: string, body: CreateRelationshipRequest) {
    return apiRequest<PersonRelationshipResponse>(`/people/${personId}/relationships`, {
      method: 'POST',
      body,
    });
  },

  deleteRelationship(relationshipId: string) {
    return apiRequest<{ deleted: true }>(`/people/relationships/${relationshipId}`, {
      method: 'DELETE',
    });
  },

  /** No session — reached straight from a link in a broadcast email. */
  unsubscribe(token: string) {
    return apiRequest<void>('/people/unsubscribe', {
      method: 'PATCH',
      body: { token },
      anonymous: true,
    });
  },

  // --- Custom fields ------------------------------------------------------------

  listCustomFieldDefinitions(includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly CustomFieldDefinitionResponse[]>(
      '/people/custom-fields/definitions',
      {
        query: { includeArchived },
        signal,
      },
    );
  },

  createCustomFieldDefinition(body: CreateCustomFieldDefinitionRequest) {
    return apiRequest<CustomFieldDefinitionResponse>('/people/custom-fields/definitions', {
      method: 'POST',
      body,
    });
  },

  updateCustomFieldDefinition(id: string, body: UpdateCustomFieldDefinitionRequest) {
    return apiRequest<CustomFieldDefinitionResponse>(`/people/custom-fields/definitions/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  archiveCustomFieldDefinition(id: string) {
    return apiRequest<{ archived: true }>(`/people/custom-fields/definitions/${id}/archive`, {
      method: 'POST',
    });
  },

  restoreCustomFieldDefinition(id: string) {
    return apiRequest<{ restored: true }>(`/people/custom-fields/definitions/${id}/restore`, {
      method: 'POST',
    });
  },

  getPersonCustomFields(personId: string, signal?: AbortSignal) {
    return apiRequest<readonly PersonCustomFieldResponse[]>(`/people/custom-fields/${personId}`, {
      signal,
    });
  },

  setPersonCustomFields(personId: string, values: Readonly<Record<string, unknown>>) {
    return apiRequest<readonly PersonCustomFieldResponse[]>(`/people/custom-fields/${personId}`, {
      method: 'PUT',
      body: { values },
    });
  },

  // --- Contacts (Sección de Contacto) --------------------------------------

  addContact(personId: string, body: CreatePersonContactRequest) {
    return apiRequest<PersonDetailResponse>(`/people/${personId}/contacts`, {
      method: 'POST',
      body,
    });
  },

  updateContact(personId: string, contactId: string, body: UpdatePersonContactRequest) {
    return apiRequest<PersonDetailResponse>(`/people/${personId}/contacts/${contactId}`, {
      method: 'PATCH',
      body,
    });
  },

  removeContact(personId: string, contactId: string) {
    return apiRequest<PersonDetailResponse>(`/people/${personId}/contacts/${contactId}`, {
      method: 'DELETE',
    });
  },

  // --- Catalogs (Profesión / Ocupación / Sector Económico) -----------------

  listCatalog(kind: PersonCatalogKind, includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly PersonCatalogEntryResponse[]>(`/people/catalogs/${kind}`, {
      query: { includeArchived },
      signal,
    });
  },

  createCatalogEntry(kind: PersonCatalogKind, name: string) {
    return apiRequest<PersonCatalogEntryResponse>(`/people/catalogs/${kind}`, {
      method: 'POST',
      body: { name },
    });
  },
};
